import { AfterViewInit, ChangeDetectorRef, Component, HostListener, inject, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  GridColumn,
  GridStateChangeV2,
  ReactiveComponent,
  ModalService,
  ToastV2Service,
  ControlButton,
  MachineService,
  GridColumnV2,
  MultiselectV2SimpleItemSource,
  GridNoDataConfigV2,
  UserRightsService,
  NotificationsV2Module,
  RadioV2Module,
  GridV2Module,
  IconV2Module,
  ServiceErrorV2Component,
} from '@schenck/rotec-components';
import { switchMap, takeUntil } from 'rxjs/operators';
import { translateComponent } from 'src/components/components-and-function-group';
import { createSchedulingFilter } from 'src/components/task-interval.pipe';
import { addTitleAndWidthToGridColumn, PartialColumn } from 'src/pages/config-utils';
import { DownloadService, MachineTaskService } from 'src/services';
import { MachineHistorySearchService } from '../machine-history-search.service';
import { machineHistoryColumnsBaseConfig } from '../machine-history-settings.api';
import { MachineHistorySettingsService } from '../machine-history-settings.service';
import { MachineHistory } from '../machine-history.model';
import { MachineHistoryModalComponent } from '../machine-history-modal/machine-history-modal.component';
import { openErrorToast } from 'src/utils';
import { AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { PriorityComponent } from '../../../components/priority/priority.component';
import { ExecutionDateComponent } from '../../../components/execution-date/execution-date.component';
import { DateComponent } from '../../../components/date/date.component';
import { TaskIntervalPipe } from '../../../components/task-interval.pipe';
import { TypeEnumPipe } from '../../../components/type-enum.pipe';
import { TaskComponentPipe } from '../../../components/components-and-function-group';

@Component({
  selector: 'ma-machine-history',
  templateUrl: './machine-history.component.html',
  styleUrls: ['./machine-history.component.less'],
  imports: [
    NotificationsV2Module,
    RadioV2Module,
    GridV2Module,
    IconV2Module,
    PriorityComponent,
    ExecutionDateComponent,
    DateComponent,
    AsyncPipe,
    TaskIntervalPipe,
    TypeEnumPipe,
    TaskComponentPipe,
    ServiceErrorV2Component,
  ],
})
export class MachineHistoryComponent extends ReactiveComponent implements AfterViewInit {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly taskService = inject(MachineTaskService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly downloadService = inject(DownloadService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastV2Service);
  private readonly machineService = inject(MachineService);
  public settingsService = inject(MachineHistorySettingsService);
  public searchService = inject(MachineHistorySearchService);
  public userRightsService = inject(UserRightsService);

  public gridHeight = 500;
  public searchText = '';
  public gridScrollContainer = this.document;
  public hasPagination = false;
  public selectedElement: string;
  public undoLoadingState: WeakMap<MachineHistory, boolean> = new WeakMap();

  public noDataText: GridNoDataConfigV2 = {
    withFilter: {
      text: 'There are no entries for the selected filters. Remove or change filters to find entries.',
      buttonText: 'Remove all filters',
      buttonType: 'secondary',
    },
    withSearch: {
      text: 'There are no results for your search. Change the search query to get matching results.',
    },
    withoutFilter: {
      text: 'There are currently no entries.',
      buttonText: null,
      buttonType: null,
      hidden: true,
    },
  };

  public state = this.connect(
    {
      searchData: this.searchService.state$,
      columns: this.onInit$.pipe(switchMap(() => this.createColumns())),
      responsibleCompanyFilterValue: this.searchService.responsibleCompanyFilterValue$,
    },
    this.cdr
  );

  public controlButtons: ControlButton[] = [
    {
      icon: 'file-pdf',
      label: 'COMMON_Export',
      click: (_) => {
        this.export();
      },
    },
  ];

  constructor() {
    super();
  }

  public ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setGridHeight();

    this.searchService.state$.pipe(takeUntil(this.onDestroy$)).subscribe((state) => {
      this.hasPagination = state.total > state.pageSize;
      this.setGridHeight();
    });
  }

  @HostListener('window:resize') public setGridHeight() {
    const header = this.document.querySelector('.header-row');
    const titleHeight = 75;
    const navbarHeight = this.document.querySelector('.pages-navbar')?.clientHeight;
    const paginationSize = this.hasPagination ? 80 : 0;
    this.gridHeight =
      window.innerHeight - titleHeight - navbarHeight - header.clientHeight - 250 - paginationSize;
  }

  public isReactivateBtnDisabled(task: MachineHistory): boolean {
    return this.machineService.state[task.machineId]?.isDeactivated || !task.serialTask?.isActive;
  }

  public reactToPageSizeChange(pageSize: number): void {
    this.searchService.search({ pageSize });
  }

  public refineSearch(state: GridStateChangeV2): void {
    this.searchService.search({
      filter: <any>state.filter,
      fulltext: state.fulltext,
      page: state.page,
      sort: <any>state.sort,
    });
  }

  public keyupEnterSearch(searchText: string): void {
    this.searchService.search({ fulltext: searchText, page: 0 });
    this.searchText = '';
  }

  public updateColumnSettings(columns: GridColumn[]): void {
    this.settingsService.updateColumnSettings(columns);
  }

  public openTask(task: MachineHistory): void {
    this.selectedElement = this.selectedElement === task.id ? '' : task.id;
    this.modalService.open<string>(MachineHistoryModalComponent, task.id);
  }

  public undoTask(task: MachineHistory, event: Event): void {
    event.stopPropagation();
    this.undoLoadingState.set(task, true);
    this.taskService
      .undo$(task.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        openErrorToast(this.toastService)(response);
        this.undoLoadingState.delete(task);
        this.cdr.detectChanges();
      });
  }

  public export(): void {
    this.downloadService.downloadExecutedMachineTaskPDF(this.state.searchData.selectedIds);
  }

  public isSelected(id: string): boolean {
    return this.state.searchData.selectedIds.includes(id) || this.selectedElement === id;
  }

  public toggleSelectedItem($event) {
    this.selectedElement = $event?.id;
  }

  private createColumns(): Observable<GridColumnV2<string>[]> {
    const columnDefs: PartialColumn[] = [
      {
        key: 'undo',
        sortable: false,
        notResizable: true,
        staticWidth: true,
        notConfigurable: true,
        title: '',
      },
      {
        key: 'plantName',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('plantName') },
      },
      {
        key: 'machineName',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('machineName') },
      },
      {
        key: 'machineSerial',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('machineSerial') },
      },
      {
        key: 'schenckId',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'customerId',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'title',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'priority',
        sortable: true,
        filter: {
          type: 'checkbox',
          showAllCheckbox: true,
          options: [
            { name: 'High', value: 'High' },
            { name: 'Medium', value: 'Medium' },
            { name: 'Low', value: 'Low' },
          ],
        },
      },
      {
        key: 'responsibleCompany',
        sortable: true,
        filter: {
          type: 'radio',
          options: [
            { value: 'Customer', name: 'My' },
            { value: 'Schenck', name: 'Schenck' },
          ],
          displayAllOption: true,
        },
      },
      {
        key: 'responsible',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('responsible'),
        },
      },
      {
        key: 'executive',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('executive'),
        },
      },
      {
        key: 'executionDate',
        sortable: true,
        filter: { type: 'date' },
      },
      {
        key: 'dueDate',
        sortable: true,
        filter: { type: 'date' },
      },
      {
        key: 'description',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'component',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('component', (value) => {
            return translateComponent(value, this.translate);
          }),
        },
      },
      {
        key: 'type',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('type'), withCustomValue: false },
      },
      {
        key: 'requiredTooling',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'possibleSpareParts',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'expectedDurationMinutes',
        sortable: true,
      },
      {
        key: 'scheduling',
        sortable: true,
        filter: createSchedulingFilter(this.searchService.allResults$, this.translate),
      },
    ];

    return this.settingsService.createColumns$(
      addTitleAndWidthToGridColumn(columnDefs, machineHistoryColumnsBaseConfig)
    );
  }

  private getItemSource(
    entity: keyof MachineHistory,
    translateFn?: (value: any) => string
  ): MultiselectV2SimpleItemSource<any> {
    return this.searchService.getMultiselectItemSource(entity, translateFn);
  }
}
